import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Dashboard.css';
import { API_BASE } from '../config/apiConfig';

const Dashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('date-desc');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [jurisdictionLabel, setJurisdictionLabel] = useState('CityLink');
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubmissions();
  }, []);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilterDropdown && !event.target.closest('.filter-wrapper')) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterDropdown]);

  // Filter and sort submissions
  useEffect(() => {
    let filtered = [...submissions];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(sub => {
        // Search by Submission ID
        if (sub.id.toLowerCase().includes(query)) return true;
        
        // Search by Tyler ID
        if (sub.tylerId && sub.tylerId.toLowerCase().includes(query)) return true;
        
        // Search by Status
        if (getStatusLabel(sub.status).toLowerCase().includes(query)) return true;
        
        // Search by form data fields (if available)
        if (sub.data) {
          const data = sub.data;
          // Owner name
          if (data.ownerName && data.ownerName.toLowerCase().includes(query)) return true;
          // Owner email
          if (data.ownerEmail && data.ownerEmail.toLowerCase().includes(query)) return true;
          // Street address
          if (data.streetAddress && data.streetAddress.toLowerCase().includes(query)) return true;
          // City
          if (data.city && data.city.toLowerCase().includes(query)) return true;
          // Contractor name
          if (data.contractorName && data.contractorName.toLowerCase().includes(query)) return true;
          // Business name
          if (data.businessName && data.businessName.toLowerCase().includes(query)) return true;
        }
        
        return false;
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'rejected') {
        filtered = filtered.filter(sub => sub.isRejected);
      } else {
        filtered = filtered.filter(sub => sub.status === statusFilter && !sub.isRejected);
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortOrder) {
        case 'az':
          return a.id.localeCompare(b.id);
        case 'za':
          return b.id.localeCompare(a.id);
        case 'date-desc':
          return new Date(b.timestamp) - new Date(a.timestamp);
        case 'date-asc':
          return new Date(a.timestamp) - new Date(b.timestamp);
        default:
          return new Date(b.timestamp) - new Date(a.timestamp);
      }
    });

    setFilteredSubmissions(filtered);
  }, [submissions, searchQuery, statusFilter, sortOrder]);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/submissions`);
      const submissionsList = response.data.submissions || [];
      // Sort by timestamp descending (newest first)
      let sortedSubmissions = submissionsList.sort((a, b) => {
        return new Date(b.timestamp) - new Date(a.timestamp);
      });

      // Filter by current selected location (city/county) so users only see submissions for that jurisdiction
      const selectedLocationRaw = localStorage.getItem('selectedCounty');
      if (selectedLocationRaw) {
        try {
          const loc = JSON.parse(selectedLocationRaw);
          // Only show city in the dashboard title; fall back to CityLink if missing
          setJurisdictionLabel(loc.city || 'CityLink');
          sortedSubmissions = sortedSubmissions.filter(sub => {
            const data = sub.data || {};
            // Prefer explicit jurisdiction fields if present
            if (data.jurisdictionCity || data.jurisdictionCounty) {
              return (
                (!loc.city || data.jurisdictionCity === loc.city) &&
                (!loc.county || data.jurisdictionCounty === loc.county)
              );
            }
            // Fallback: match on project city if jurisdiction not stored (older submissions)
            if (data.city) {
              return !loc.city || data.city === loc.city;
            }
            return true; // If no data, keep (likely legacy/demo)
          });
        } catch (err) {
          console.error('Failed to parse selectedCounty for dashboard filtering:', err);
        }
      }

      setSubmissions(sortedSubmissions);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
      setLoading(false);
    }
  };

  const getStatusColor = (status, isRejected) => {
    if (isRejected) return '#f97373';
    const colors = {
      submitted: '#3b82f6',
      under_review: '#f59e0b',
      department_coordination: '#8b5cf6',
      decision: '#10b981',
      issued: '#22c55e'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusLabel = (status) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="dashboard-shell">
      {/* Top form header */}
      <div className="dashboard-topbar">
        <div className="dashboard-form-meta">
          <div className="dashboard-form-title">
            {jurisdictionLabel} Permits
          </div>
          <div className="dashboard-form-subtitle">
            {filteredSubmissions.length} of {submissions.length} submissions · Live
          </div>
        </div>
        <Link to="/submit" className="primary-button subtle">
          New application
        </Link>
      </div>

      {/* Tab navigation (static except Submissions) */}
      <div className="dashboard-tabs">
        <button className="dashboard-tab active">Submissions</button>
        <button className="dashboard-tab">Setup</button>
        <button className="dashboard-tab">Analytics</button>
        <button className="dashboard-tab">Settings</button>
      </div>

      {/* Toolbar row */}
      <div className="dashboard-toolbar">
        <div className="toolbar-left">
          <span className="date-label">Today</span>
          <div className="search-wrapper">
            <span className="search-icon" aria-hidden="true"></span>
            <input 
              type="text" 
              className="search-input" 
              placeholder="Q Search.."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="toolbar-right">
          <div className="filter-wrapper">
            <button 
              className="filter-button"
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            >
              <span>Filters</span>
              {statusFilter !== 'all' && <span className="filter-badge">{statusFilter === 'rejected' ? 'Rejected' : getStatusLabel(statusFilter)}</span>}
            </button>
            {showFilterDropdown && (
              <div className="filter-dropdown">
                <div className="filter-option" onClick={() => { setStatusFilter('all'); setShowFilterDropdown(false); }}>
                  <input type="radio" checked={statusFilter === 'all'} readOnly />
                  <label>All</label>
                </div>
                <div className="filter-option" onClick={() => { setStatusFilter('submitted'); setShowFilterDropdown(false); }}>
                  <input type="radio" checked={statusFilter === 'submitted'} readOnly />
                  <label>Submitted</label>
                </div>
                <div className="filter-option" onClick={() => { setStatusFilter('under_review'); setShowFilterDropdown(false); }}>
                  <input type="radio" checked={statusFilter === 'under_review'} readOnly />
                  <label>Under Review</label>
                </div>
                <div className="filter-option" onClick={() => { setStatusFilter('department_coordination'); setShowFilterDropdown(false); }}>
                  <input type="radio" checked={statusFilter === 'department_coordination'} readOnly />
                  <label>Department Coordination</label>
                </div>
                <div className="filter-option" onClick={() => { setStatusFilter('decision'); setShowFilterDropdown(false); }}>
                  <input type="radio" checked={statusFilter === 'decision'} readOnly />
                  <label>Decision</label>
                </div>
                <div className="filter-option" onClick={() => { setStatusFilter('issued'); setShowFilterDropdown(false); }}>
                  <input type="radio" checked={statusFilter === 'issued'} readOnly />
                  <label>Issued</label>
                </div>
                <div className="filter-option" onClick={() => { setStatusFilter('rejected'); setShowFilterDropdown(false); }}>
                  <input type="radio" checked={statusFilter === 'rejected'} readOnly />
                  <label>Rejected</label>
                </div>
              </div>
            )}
          </div>
          <div className="sort-wrapper">
            <button 
              className="sort-button"
              onClick={() => {
                const orders = ['date-desc', 'date-asc', 'az', 'za'];
                const currentIndex = orders.indexOf(sortOrder);
                setSortOrder(orders[(currentIndex + 1) % orders.length]);
              }}
            >
              <span>
                {sortOrder === 'az' && 'Sort by A-Z'}
                {sortOrder === 'za' && 'Sort by Z-A'}
                {sortOrder === 'date-desc' && 'Sort by Date (Newest)'}
                {sortOrder === 'date-asc' && 'Sort by Date (Oldest)'}
              </span>
              <span className="sort-icon">⇅</span>
            </button>
          </div>
          <button 
            className={`view-toggle-button ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
            title={viewMode === 'list' ? 'Switch to Grid View' : 'Switch to List View'}
          >
            {viewMode === 'list' ? (
              <span className="grid-icon">⊞</span>
            ) : (
              <span className="list-icon">≡</span>
            )}
          </button>
        </div>
      </div>

      {/* Submissions list */}
      <div className="dashboard-list-card">
        {loading ? (
          <p className="loading-state">Loading...</p>
        ) : submissions.length === 0 ? (
          <p className="empty-state">
            No submissions yet. <Link to="/submit">Create your first application</Link>
          </p>
        ) : filteredSubmissions.length === 0 ? (
          <p className="empty-state">
            No submissions match your search criteria.
          </p>
        ) : viewMode === 'list' ? (
          <div className="submissions-list">
            <div className="submissions-list-header">
              <div className="list-col name">Form name</div>
              <div className="list-col status">Status</div>
              <div className="list-col tyler">Tyler ID</div>
              <div className="list-col date">Submitted</div>
            </div>
            <div className="submissions-list-body">
              {filteredSubmissions.map((sub) => {
                const statusColor = getStatusColor(sub.status, sub.isRejected);
                const shortId = sub.id.substring(0, 8);
                const initial = shortId[0]?.toUpperCase() || 'S';
                return (
                  <button
                    key={sub.id}
                    className="list-row"
                    onClick={() => navigate(`/status/${sub.id}`)}
                  >
                    <div className="list-cell name">
                      <div
                        className="list-icon"
                        style={{ backgroundColor: `${statusColor}22` }}
                      >
                        <span>{initial}</span>
                      </div>
                      <div className="list-text">
                        <div className="list-title">Submission {shortId}...</div>
                        <div className="list-subtitle">
                          {sub.isRejected
                            ? sub.rejectionReason || 'Rejected'
                            : getStatusLabel(sub.status)}
                        </div>
                      </div>
                    </div>
                    <div className="list-cell status">
                      <span
                        className="status-pill-table"
                        style={{ backgroundColor: statusColor }}
                      >
                        {getStatusLabel(sub.status)}
                      </span>
                    </div>
                    <div className="list-cell tyler">
                      {sub.tylerId || <span className="pending">Pending</span>}
                    </div>
                    <div className="list-cell date">
                      {new Date(sub.timestamp).toLocaleString()}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="submissions-grid">
            {filteredSubmissions.map((sub) => {
              const statusColor = getStatusColor(sub.status, sub.isRejected);
              const shortId = sub.id.substring(0, 8);
              const initial = shortId[0]?.toUpperCase() || 'S';
              return (
                <div
                  key={sub.id}
                  className="grid-card"
                  onClick={() => navigate(`/status/${sub.id}`)}
                >
                  <div className="grid-card-header">
                    <div
                      className="grid-icon-large"
                      style={{ backgroundColor: `${statusColor}22` }}
                    >
                      <span>{initial}</span>
                    </div>
                    <span
                      className="status-pill-grid"
                      style={{ backgroundColor: statusColor }}
                    >
                      {getStatusLabel(sub.status)}
                    </span>
                  </div>
                  <div className="grid-card-body">
                    <div className="grid-card-title">Submission {shortId}...</div>
                    <div className="grid-card-subtitle">
                      {sub.isRejected
                        ? sub.rejectionReason || 'Rejected'
                        : getStatusLabel(sub.status)}
                    </div>
                    <div className="grid-card-meta">
                      <div className="grid-meta-item">
                        <span className="grid-meta-label">Tyler ID:</span>
                        <span className="grid-meta-value">
                          {sub.tylerId || 'Pending'}
                        </span>
                      </div>
                      <div className="grid-meta-item">
                        <span className="grid-meta-label">Submitted:</span>
                        <span className="grid-meta-value">
                          {new Date(sub.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
